// src/components/clients/types.ts

export const CLIENT_STATUS_VALUES = [
  "new_client", "touring", "applied", "payment_received", "closed", "out_of_contact",
] as const;
export type ClientStatus = typeof CLIENT_STATUS_VALUES[number];
export const CLIENT_NAVIGATION_STATUS_VALUES = [
  ...CLIENT_STATUS_VALUES, "invoice_sent",
] as const;
export type ClientNavigationStatus = typeof CLIENT_NAVIGATION_STATUS_VALUES[number];
export type ClientType = "renter" | "buyer" | "landlord" | "investor";
export type ClientPriority = "low" | "normal" | "high" | "urgent";
export type MainTab = "leads" | "active" | "out_of_contact";
export type ViewMode = "grid" | "list" | "pipeline" | "needs_touch";
export type TaskStatus = "pending" | "completed" | "skipped" | "cancelled";

// Touch channel for a task (migration 041). Drives the channel icon + the
// contact-activity mirror written on complete.
export type TaskChannel = "text" | "call" | "email" | "tour" | "task";

export interface Agent {
  id: string;
  full_name: string;
  email: string;
  avatar_url?: string;
  role: string;
}

export interface Client {
  is_synthetic?: boolean | null;
  /** F24 (migration 122) · operable test client, excluded from every count. */
  is_test_client?: boolean | null;
  id: string;
  first_name: string;
  last_name: string;
  /**
   * Optional as of 2026-08-06, reconciling this with `src/types/client.ts`,
   * which has always declared `email?: string`. Two interfaces named `Client`
   * disagreed about whether this field can be absent, and THIS one — the
   * non-optional side — is why `tsc` could not see the unguarded
   * `c.email.toLowerCase()` calls that would have taken down client search.
   *
   * It is also about to be true in the database: the identity relaxation
   * (name + email OR phone) makes `clients.email` nullable so phone-only
   * walk-ins can be entered at all. A type that promises more than the schema
   * does is not a convenience, it is the compiler agreeing with a bug.
   */
  email?: string;
  phone?: string;
  status: ClientStatus;
  client_type: ClientType;
  priority: ClientPriority;
  max_budget?: number;
  min_bedrooms?: number;
  preferred_areas?: string[];
  notes?: string;
  lead_source?: string;
  move_in_timeline?: string;
  move_in_date?: string | null;
  employer?: string;
  income_range?: string;
  last_contacted_at?: string;
  created_at: string;
  assigned_agent_id?: string;
  assigned_agent?: Agent;
}

export interface Lead {
  is_synthetic?: boolean | null;
  /** F24 (migration 122) · operable test client, excluded from every count. */
  is_test_client?: boolean | null;
  id: string;
  created_at: string;
  name: string;
  email: string;
  phone?: string;
  apartment_size?: string;
  budget?: string;
  budget_number?: number;
  neighborhoods?: string[] | string | null;
  neighborhood_other?: string | null;
  pets?: string | null;
  move_in_date?: string;
  credit_status?: string;
  employment_status?: string;
  notes?: string;
  email_list_opt_in?: boolean;
  /** Ch78 · the Website form's SMS checkbox (leads.sms_opted_in). true =
   * asked to be texted; false/null = not asked (NOT a refusal). Read via
   * lib/leadSmsOptIn.ts so every surface says the same thing. */
  sms_opted_in?: boolean | null;
  source?: string;
  source_channel?: string;
  source_platform?: string;
  source_referrer_host?: string;
  source_utm_source?: string;
  source_utm_medium?: string;
  source_utm_campaign?: string;
  source_utm_content?: string;
  source_utm_term?: string;
  source_landing_path?: string;
  verification_state?: string;
  verification_score?: number;
  verification_label?: string;
  verification_flags?: Record<string, any>;
  verification_reasons?: string[];
  verification_provider?: string;
  verification_checked_at?: string;
  status: string;
}

export interface ClientActivity {
  id: string;
  activity_type: string;
  description: string;
  metadata?: any;
  created_at: string;
}

export interface ClientNote {
  id: string;
  client_id: string;
  content: string;
  created_at: string;
  created_by: string;
}

export interface ShortlistQuestionPayload {
  question_text: string;
  deleted_at?: string | null;
}

export interface ClientTask {
  id: string;
  client_id: string;
  task_type: string;
  title: string;
  description?: string;
  status: TaskStatus;
  due_date?: string;
  completed_at?: string;
  created_at: string;
  created_by: string;
  // Cadence provenance (migration 041). NULL/'manual' for ad-hoc operator tasks.
  source?: "manual" | "cadence" | "signal";
  cadence_key?: string | null;
  cadence_step?: string | null;
  channel?: TaskChannel | null;
  skipped_at?: string | null;
  snoozed_from?: string | null;
  version?: number;
  owner_user_id?: string | null;
  source_event_id?: string | null;
  priority?: "urgent" | "high" | "normal" | "low" | null;
  due_at?: string | null;
  acknowledge_by?: string | null;
  acknowledged_at?: string | null;
  complete_by?: string | null;
  outcome_code?: string | null;
  completion_note?: string | null;
  proof_ref?: string | null;
  escalation_state?: "none" | "ack_overdue" | "overdue";
  question_payload?: ShortlistQuestionPayload | ShortlistQuestionPayload[] | null;
}

export interface SentListing {
  id: string;
  client_id: string;
  recipient_email: string;
  sent_at: string;
  tracking_id?: string;
  email_html?: string;
  property_count: number;
  unit_count: number;
  properties?: any[];
  subject?: string;
  locator?: any;
  personal_message?: string;
}

export interface SentGuestCard {
  id: string;
  client_id: string;
  property_id?: string;
  tracking_id?: string;
  recipient_name: string;
  recipient_email: string;
  property_snapshot?: {
    name?: string;
    neighborhood?: string;
  };
  email_html?: string;
  /**
   * Written on every insert since migration 059 (NOT NULL DEFAULT 'emailed',
   * CHECK IN ('emailed','downloaded')) but never present on this type, so no
   * reader could tell an emailed card from a hand-submitted registration —
   * which is why the files list labelled every row "Guest card email".
   */
  delivery_method?: "emailed" | "downloaded";
  sent_at: string;
}

export interface SavedSearch {
  id: string;
  name: string;
  search_criteria: any;
  result_count: number;
  created_at: string;
}
